import { css, keyframes, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { mq, overflowEllipsis, shouldForwardProp } from '@hrworks/design-system';
import { HelpText as _HelpText, type HelpTextProps } from '@hrworks/sui-shared/components';
import type { Size } from '@hrworks/sui-shared/types';
import type { SetRequired } from 'type-fest';

import { ButtonStyles } from '../Button';
import type { InputFieldProps } from './InputField.types';

const componentConfig = (theme?: Theme) =>
  ({
    buttonFontSize: '0.875em',
    inputOutlineWidth: 1,
    floatingLabelFontSize: '0.857em',
    inputFontSize: '1em',
    offsetX: {
      extraSmall: 12,
      small: 14,
      medium: 16,
      large: 18,
      extraLarge: 20,
    },
    offsetY: {
      extraSmall: 8,
      small: 10,
      medium: 12,
      large: 14,
      extraLarge: 16,
    },
    ...(theme && {
      inputLineHeight: theme.sqwTier2Typography.bodyMd.lineHeight,
    }),
  }) as const;

const generateInputBoxShadowStyles = ({ color, width = 1 }: { color: string; width?: number }) =>
  `0px 0px 0px ${width}px inset ${color}`;

const Container = styled.div<{ size: Size }>(({ theme, size }) => ({
  position: 'relative',
  fontSize: theme.marko.typography.sqwFontSizes[size],
  [mq.conditionalTransition]: {
    transitionProperty: 'all',
    transitionDuration: theme.marko.variables.animationDuration.normal,
  },
}));

const InputContainer = styled.div({
  position: 'relative',
});

const floatingLabelStyles = (
  theme: Theme,
  size: Required<InputFieldProps>['size'],
  labelPlacement: InputFieldProps['labelPlacement'],
) => {
  const { offsetY, floatingLabelFontSize, inputOutlineWidth, inputFontSize, inputLineHeight } = componentConfig(theme);

  // Solves a bug for the onClick on the ComboBox where it wouldnt trigger the onclick.
  const enablePointerEvents = keyframes({
    100: {
      pointerEvents: 'all',
    },
  });

  return css({
    ...theme.sqwTier2Typography.bodySm,
    fontSize: floatingLabelFontSize,
    top: 0,
    cursor: 'default',
    animation: `${enablePointerEvents} ${theme.marko.variables.animationDuration.normal}`,

    ...(labelPlacement === 'inside' && {
      top: (() => {
        const labelOffset = `${floatingLabelFontSize} / 2`;
        const gapBetweenLabelAndInputText = `(${inputLineHeight} * ${inputFontSize} - ${inputFontSize}) / 2`;

        return `calc(${offsetY[size] / 2}px + ${labelOffset} + ${inputOutlineWidth}px + ${gapBetweenLabelAndInputText})`;
      })(),
    }),
  });
};

type LabelProps = {
  isLabelFloating: boolean;
  size: Size;
} & SetRequired<
  Pick<InputFieldProps, 'hasButton' | 'hasInputFocus' | 'disabled' | 'validationState' | 'labelPlacement'>,
  'labelPlacement'
>;

const Label = styled.label<LabelProps>(
  ({ theme, isLabelFloating, size, hasButton, hasInputFocus, disabled, validationState, labelPlacement }) => {
    const { offsetX, buttonFontSize } = componentConfig(theme);

    return [
      {
        [mq.conditionalTransition]: {
          transitionProperty: 'top, max-width',
          transitionDuration: theme.marko.variables.animationDuration.normal,
        },
        '*': {
          transitionProperty: 'font-size, color',
          transitionDuration: theme.marko.variables.animationDuration.normal,
        },
        display: 'inline-flex',
        position: 'absolute',
        alignItems: 'center',
        gap: '0.375em',
        cursor: 'text',
        fontSize: '1em',
        padding: '0 0.666em',
        zIndex: 2,
        pointerEvents: 'none',
        top: '50%',
        transform: 'translateY(-50%)',
        left: `calc(${offsetX[size]}px - 0.666em)`,
        maxWidth: `calc(100% - 2 * 0.666em)`,
        borderRadius: 6,
        color: theme.sqwTier2Color.text.subtlest,

        ...(labelPlacement === 'floating' && { backgroundColor: theme.sqwTier2Color.background.input }),

        ...(hasInputFocus && {
          color: theme.sqwTier2Color.text.selected,
        }),

        ...(validationState && {
          color: theme.sqwTier2Color.text[validationState === 'danger' ? 'error' : validationState].default,
        }),

        ...(disabled && {
          color: theme.sqwTier2Color.text.disabled,
          backgroundColor: theme.sqwTier2Color.background.disabled.subtle,
        }),

        ...(hasButton &&
          (!isLabelFloating || labelPlacement === 'inside') && {
            maxWidth: `calc(100% -  (${buttonFontSize} + 2 *
            ${offsetX[size]}px ))`,
          }),
      },
      isLabelFloating && floatingLabelStyles(theme, size, labelPlacement),
    ];
  },
);

const LabelText = styled.div(overflowEllipsis);

const HelpText = styled(_HelpText, {
  shouldForwardProp,
})<SetRequired<HelpTextProps, 'size'>>(({ theme, size }) => {
  const { offsetX } = componentConfig(theme);

  return {
    paddingLeft: offsetX[size],
    paddingRight: offsetX[size],
  };
});

const inputStyles = (
  theme: Theme,
  validationState: InputFieldProps['validationState'],
  size: Size,
  labelPlacement: Required<InputFieldProps>['labelPlacement'],
  hasButton?: boolean,
  disabled?: boolean,
) => {
  const { offsetX, offsetY, buttonFontSize, inputLineHeight, inputFontSize, inputOutlineWidth, floatingLabelFontSize } =
    componentConfig(theme);

  return css({
    display: 'block',
    width: '100%',
    color: theme.sqwTier2Color.text.default,
    border: 0,
    borderRadius: 6,
    outline: `${inputOutlineWidth}px solid transparent`,
    backgroundColor: theme.sqwTier2Color.background.input,
    padding: `${offsetY[size]}px ${offsetX[size]}px`,
    boxShadow: generateInputBoxShadowStyles({ color: theme.sqwTier2Color.border.input }),
    lineHeight: inputLineHeight,
    fontSize: inputFontSize,

    ...(labelPlacement === 'inside' && {
      paddingTop: `calc(${offsetY[size]}px + ${floatingLabelFontSize})`,
      borderRadius: offsetY[size],
    }),

    '::placeholder, & ::placeholder': {
      color: theme.sqwTier2Color.text.subtlest,
      opacity: 1,
    },

    [mq.conditionalTransition]: {
      transitionProperty: 'color, outline, border-color, box-shadow',
      transitionDuration: theme.marko.variables.animationDuration.normal,
    },

    ':focus,:focus-within': {
      boxShadow: generateInputBoxShadowStyles({ color: theme.sqwTier2Color.border.selected, width: 2 }),
    },

    ...(hasButton && {
      paddingRight: `calc(
          ${buttonFontSize} + 2 *
            ${offsetX[size]}px
        )`,
    }),

    ...(validationState &&
      (() => {
        const validationColor = theme.sqwTier2Color.border[validationState === 'danger' ? 'error' : validationState];

        return {
          boxShadow: generateInputBoxShadowStyles({
            color: validationColor,
          }),

          ':focus,:focus-within': {
            boxShadow: generateInputBoxShadowStyles({
              color: validationColor,
              width: 2,
            }),
          },
        };
      })()),

    ':-webkit-autofill': {
      [` ~ ${Label}`]: floatingLabelStyles(theme, size, labelPlacement),
    },

    ...(disabled && {
      cursor: 'not-allowed',
      color: theme.sqwTier2Color.text.disabled,
      backgroundColor: theme.sqwTier2Color.background.disabled.subtle,
      boxShadow: generateInputBoxShadowStyles({
        color: theme.sqwTier2Color.border.disabled,
      }),
    }),
  });
};

const buttonStyles = (theme: Theme, size: Size) => {
  const { offsetX, buttonFontSize } = componentConfig(theme);

  return css([
    ButtonStyles.generateButtonStyles({ theme, size, variant: 'subtle', corner: 'pill' }),
    {
      '&&': { position: 'absolute' },
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: `calc(1em + 1.5 * ${offsetX[size]}px)`,
      aspectRatio: '1/1',
      justifyContent: 'center',
      padding: 0,
      margin: `0 ${offsetX[size] / 4}px`,
      outlineOffset: -1,
      zIndex: 3,
      fontSize: buttonFontSize,
    },
  ]);
};

export const S = {
  componentConfig,
  Container,
  InputContainer,
  Label,
  LabelText,
  HelpText,
  inputStyles,
  buttonStyles,
} as const;
